import cn from 'classnames';
import { Formik, Form } from 'formik';
import { useState, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { CSSTransition } from 'react-transition-group';
import { useAnalytics } from 'use-analytics';

import Button from 'components/common/Button';
import FormField from 'components/common/FormField';
import Error from 'components/common/forms/Error';
import Subheading from 'components/common/Subheading';
import { introFormSchema } from 'components/shared/FormSchemas';
import { UserContext, VoucherContext, FunnelContext } from 'components/signUpFunnel/Contexts';
import SkipContainer from 'components/signUpFunnel/InternalSkip/SkipContainer';
import { TEMPO } from 'context/BrandContext';
import localStorage from 'helpers/localStorage';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useBrand from 'hooks/useBrand';
import useFeatureFlag from 'hooks/useFeatureFlag';
import useFunnelExperimentAssignment from 'hooks/useFunnelExperimentAssignment';
import useFunnelNavigation from 'hooks/useFunnelNavigation';
import useQuery from 'hooks/useQuery';
import {
  FAMILY,
  SUBMITTING_TEXT,
  INTRO_SMS_OPT_IN,
  LEAD_EVENT,
  ENABLE_INTERNAL_SKIP_FUNNEL,
} from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

import IntroSmsOptIn from './IntroSmsOptIn';

const IntroInformationForm = ({
  brand,
  isAmexUser,
  mealPlanPreference,
  weeklyMeals,
  servings,
  selectedMealKitTraits,
  hidePlanChangePage,
}) => {
  const isFamilyMealPlan = mealPlanPreference === FAMILY;
  const isInternal = useFeatureFlag(ENABLE_INTERNAL_SKIP_FUNNEL);
  const { user, setUser } = useContext(UserContext);
  const { setVoucher } = useContext(VoucherContext);
  const { funnelSlug } = useContext(FunnelContext);
  const { nextStep } = useFunnelNavigation();
  const experimentAssignment = useFunnelExperimentAssignment();

  const [zipCodeMessage, setZipCodeMessage] = useState({
    message: '',
    response: '',
    initial: true,
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const isTempo = useBrand(TEMPO);
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const errorRef = useRef();
  const { queryParams } = useQuery();
  const funnelParam = queryParams.get('funnel');

  const voucherCode = document?.querySelector('[data-voucher]')?.getAttribute('data-voucher');
  const referralCode = document?.querySelector('[data-referral]')?.getAttribute('data-referral');
  const mobileEmail = document
    .getElementById('hc-sign-up-funnel-react-app')
    ?.getAttribute('data-mobile-email');

  const onNavigate = useCallback(() => {
    navigate(nextStep.route);
  }, [navigate, nextStep]);

  const submitParams = useCallback(
    values => ({
      mealPlanSlug: mealPlanPreference,
      email: values.introEmail,
      signupZip: values.introZipCode,
      signupVoucher: voucherCode,
      mealKitTraits: selectedMealKitTraits,
      referralCode,
      amexCredit: isAmexUser,
      weeklyMeals,
      servings,
      smsOptIn: values.introSmsOptIn,
      phoneNumber: values.introSmsPhoneNumber,
      funnel: funnelSlug || funnelParam || mealPlanPreference,
    }),
    [
      mealPlanPreference,
      voucherCode,
      selectedMealKitTraits,
      referralCode,
      isAmexUser,
      weeklyMeals,
      servings,
      funnelSlug,
      funnelParam,
    ]
  );

  const onSubmit = useAnalyticsCallback(
    async (values, { setSubmitting }) => {
      const submitFn = user?.id ? RequestService.updateFunnelUser : RequestService.createUser;
      setIsSubmitting(true);

      try {
        const response = await submitFn({ params: submitParams(values) });
        setIsSubmitting(false);
        setError('');
        if (!response.ok) {
          const errorMessage = response.error;

          if (response.userSignInRedirect) {
            if (response.deepLink) {
              window.location.href = `homechef://login?email=${values.introEmail}`;
              return;
            } else {
              window.location.href = `/users/sign_in?email=${values.introEmail}&user_sign_in_redirect=true${isTempo ? '&tempo=true' : ''}`;
              return;
            }
          }

          setError(errorMessage);
          setSubmitting(false);
          return response;
        }

        onSuccess(response);

        if (response.user.pickMealsRedirectUrl) {
          window.location.href = response.user.pickMealsRedirectUrl;
          return;
        }

        onNavigate();
        setSubmitting(false);
        return response;
      } catch (error) {
        setIsSubmitting(false);
        const errorMessage = error.message;
        setError(errorMessage);
        ErrorLogging.captureException(errorMessage);

        return { ok: false, error: errorMessage };
      }
    },
    async ({ logEvent, events, callbackArgs, callbackFnResult }) => {
      if (brand) {
        const response = callbackFnResult;

        if (!response || !response.ok) return;

        const values = callbackArgs[0];
        const analyticsParams = {
          ...submitParams(values),
          ...(response && !response.ok && { error: response.error }),
          ...experimentAssignment,
        };

        logEvent(events.signUp[brand].intro.submitted, analyticsParams);
      }
    },
    [user, brand, isFamilyMealPlan, submitParams, onSuccess, onNavigate, experimentAssignment]
  );

  const handleZipCodeChange = zipCode => {
    if (zipCode.length === 5) {
      setDisableSubmit(true);
      RequestService.availableZipCode({
        params: {
          zip: zipCode,
          mealPlanSlug: mealPlanPreference,
        },
      }).then(response => {
        setDisableSubmit(false);
        if (response.ok) {
          setZipCodeMessage({
            message: response.zipCodes.message,
            response: response.ok,
            initial: false,
          });
        } else {
          setZipCodeMessage({
            message: response.error,
            response: response.ok,
            initial: false,
          });
        }
      });
    }
  };

  const onSuccess = useCallback(
    response => {
      try {
        const user = response.user;
        setUser({ ...user, hidePlanChangePage });
        setVoucher(user.signupVoucher);
        RequestService.v3.createEvents({
          params: {
            eventName: LEAD_EVENT,
            email: user.email,
            phone: user.phone,
            uuid: user.uuid,
            id: user.id,
          },
        });
        analytics.track(LEAD_EVENT, {
          email: user.email,
          externalId: user.uuid,
          phone: user.phone,
          mealPlanSlug: mealPlanPreference,
        });
        if (user.receivesSmsPromotional && !user.smsDiscount) {
          localStorage.write(INTRO_SMS_OPT_IN, true);
        } else {
          localStorage.delete(INTRO_SMS_OPT_IN);
        }
      } catch (e) {
        ErrorLogging.captureException(e.message);
      }
    },
    [mealPlanPreference, hidePlanChangePage]
  );

  return (
    <>
      <Formik
        initialValues={{
          introZipCode: user?.signupZip || '',
          introEmail: user?.email || mobileEmail || '',
          introSmsOptIn: user?.receivesSmsPromotional || false,
          introSmsPhoneNumber: user?.phone || '',
        }}
        enableReinitialize
        validationSchema={introFormSchema}
        validateOnBlur={true}
        onSubmit={onSubmit}
      >
        {formik => (
          <div className="relative mt-6 text-left">
            <Form>
              <div
                className={cn('relative rounded-2xl border border-solid p-4', {
                  'border-charcoal-60 bg-tempo-gray-100': isTempo,
                  'border-cream-120 bg-cream-110': !isTempo,
                })}
              >
                <Subheading
                  className="text-secondary mb-4 mt-0 text-left"
                  brandStylesOverrides="h5"
                >
                  Let’s set you up to customize your plan.
                </Subheading>
                <div className="flex flex-col gap-4 bp1:flex-row">
                  <div className="relative w-full">
                    <FormField
                      label="Zip Code (Required)"
                      labelClass={isTempo ? 'text-tempo-black-100' : ''}
                      maxLength={5}
                      inputMode="numeric"
                      autoComplete="false"
                      name="introZipCode"
                      inputClass={cn('input--text rounded-2xl px-3', {
                        'border-charcoal-60': isTempo,
                        'border-cream-120': !isTempo,
                      })}
                      labelBrandStylesOverrides="textMd"
                      onBlur={e => {
                        let zipCode = e.currentTarget.value;
                        handleZipCodeChange(zipCode);
                        formik.handleBlur(e);
                      }}
                      onChange={e => {
                        setZipCodeMessage({
                          message: '',
                          response: '',
                          initial: false,
                        });
                        formik.handleChange(e);
                      }}
                      successMessage={
                        zipCodeMessage.response === true ? zipCodeMessage.message : null
                      }
                      error={
                        formik.touched.introZipCode &&
                        ((zipCodeMessage.response === false && zipCodeMessage.message) ||
                          formik.errors.introZipCode)
                      }
                      required={true}
                      hideRequiredAsterisk
                      errorContainerClass="min-h-1"
                    />
                  </div>
                  <div className="relative w-full">
                    <FormField
                      type="email"
                      label="Email (Required)"
                      labelClass={isTempo ? 'text-tempo-black-100' : ''}
                      autoComplete="email"
                      name="introEmail"
                      disabled={Boolean(user?.id)}
                      inputClass={cn('input--text rounded-2xl px-3', {
                        'border-charcoal-60': isTempo,
                        'border-cream-120': !isTempo,
                      })}
                      labelBrandStylesOverrides="textMd"
                      onBlur={formik.handleBlur}
                      error={formik.touched.introEmail && formik.errors.introEmail}
                      required={true}
                      hideRequiredAsterisk
                      errorContainerClass="min-h-1"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <IntroSmsOptIn />
                </div>
              </div>
              <CSSTransition
                in={Boolean(error)}
                classNames="fadeInOut"
                timeout={300}
                unmountOnExit
                nodeRef={errorRef}
              >
                <div ref={errorRef}>
                  <Error message={error} show />
                </div>
              </CSSTransition>
              <Button
                type="submit"
                className="mt-4 w-full bp1:mt-6"
                onClick={formik.onSubmit}
                isSubmitting={isSubmitting || disableSubmit}
                disabled={zipCodeMessage.response === false || !formik.isValid}
                submittingText={disableSubmit ? 'Continue' : SUBMITTING_TEXT}
                variation="Pill"
              />
              {isInternal && (
                <SkipContainer
                  setIsSubmitting={setIsSubmitting}
                  setError={setError}
                  introSubmitParams={submitParams}
                  mealPlan={mealPlanPreference}
                />
              )}
            </Form>
          </div>
        )}
      </Formik>
    </>
  );
};

IntroInformationForm.propTypes = {
  brand: PropTypes.string,
  isAmexUser: PropTypes.bool,
  mealPlanPreference: PropTypes.string,
  weeklyMeals: PropTypes.string,
  servings: PropTypes.string,
  selectedMealKitTraits: PropTypes.array,
  hidePlanChangePage: PropTypes.bool,
};

export default IntroInformationForm;
